import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import type { VenueTime } from '../lib/time.js';

export interface VenueSummary extends VenueTime {
  id: string;
  slug: string;
  name_en: string;
  name_ar: string;
  currency: string;
  role: string | null;
}

interface VenueRow {
  id: string;
  slug: string;
  name_en: string;
  name_ar: string;
  timezone: string;
  service_day_cutoff_hour: number;
  digit_style: 'western' | 'arabic';
  currency: string;
  role: string | null;
}

/**
 * The venues this user can reach.
 *
 * Long-lived: a venue's timezone and cutoff hour change roughly never, and
 * every date on every screen depends on them, so re-fetching would be pure
 * noise on the host stand's connection.
 */
export function useVenues() {
  const { user } = useSession();

  return useQuery({
    queryKey: ['venues', user?.id],
    enabled: Boolean(user),
    staleTime: 30 * 60_000,
    queryFn: async (): Promise<VenueSummary[]> => {
      const result = await api.get<{ venues: VenueRow[] }>('/api/v1/venues');
      return result.venues.map((row) => ({
        id: row.id,
        slug: row.slug,
        name_en: row.name_en,
        name_ar: row.name_ar,
        currency: row.currency,
        role: row.role,
        // Flattened into the shape lib/time.ts expects, so no screen has to
        // remember that the API uses snake_case here.
        timezone: row.timezone,
        serviceDayCutoffHour: row.service_day_cutoff_hour,
        digitStyle: row.digit_style,
      }));
    },
  });
}

/** The active venue, or null while it loads. */
export function useVenue(): VenueSummary | null {
  const { venueId } = useSession();
  const { data } = useVenues();
  return data?.find((venue) => venue.id === venueId) ?? null;
}
